<script lang="ts" setup>
import { Modal, Tooltip } from "@/components";
import {
  useSpaceSectionsQuery,
  useDeleteSpaceSectionMutation,
} from "@/graphql/operations";
import { computed, ref } from "vue";

const props = defineProps({
  sectionId: {
    type: String,
    required: true,
  },
  spaceId: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(["close", "success"]);

const { data, fetching } = useSpaceSectionsQuery({
  variables: {
    spaceId: props.spaceId,
  },
});

const sections = computed(() => data.value?.documentSpaceSections?.nodes);

const saveError = ref(false);
const { executeMutation, fetching: deleting } = useDeleteSpaceSectionMutation();
const removeSection = async (target?: string) => {
  if (deleting.value || target === props.sectionId) return;
  saveError.value = false;
  const res = await executeMutation({
    sectionId: props.sectionId,
    moveDocumentsToSectionId: target,
  });
  if (res.error || !res.data?.deleteDocumentSpaceSection) {
    saveError.value = true;
    return;
  }
  emits("success");
  emits("close");
};
const deleteWithDocuments = () => removeSection();
const moveToSection = (id: string) => removeSection(id);
</script>

<template>
  <Modal :active="true" :closeDisabled="deleting" @close="emits('close')">
    <template #header>
      {{ $t("documentSpace.sections.deleteWithDocuments.title") }}
    </template>
    <template #body>
      <p v-if="saveError" role="alert" class="save_error">{{ $t("error") }}</p>
      <h4>
        {{
          $t("documentSpace.sections.deleteWithDocuments.moveToOtherSection")
        }}:
      </h4>
      <div>
        <div v-for="section in sections" :key="section!.id" class="row">
          {{
            section!.title
              ? section!.title
              : $t("documentSpace.documents.title")
          }}

          <Tooltip class="flex_right right">
            <template v-slot>
              <button
                class="button primary"
                :class="{ disabled: section!.id === sectionId || deleting }"
                :disabled="section!.id === sectionId || deleting"
                @click="moveToSection(section!.id)"
              >
                <span class="material-symbols-outlined"> place_item </span>
              </button>
            </template>
            <template #tooltip v-if="section!.id !== sectionId">
              {{ $t("documentSpace.sections.deleteWithDocuments.moveTo") }}
            </template>
          </Tooltip>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="buttons">
        <button class="button" :disabled="deleting" @click="emits('close')">
          {{ $t("cancel") }}
        </button>
        <button
          class="button dangerous"
          :disabled="deleting"
          @click="deleteWithDocuments"
        >
          <span class="material-symbols-outlined"> delete </span>
          {{ $t("documentSpace.sections.deleteWithDocuments.deleteAll") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.save_error {
  color: var(--classic-danger, #c62828);
  font-size: 0.9em;
}
.row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--classic-border, #d9d9d9);
  padding: 0.6em 0;
}

.row .flex_right {
  margin-left: auto;
}

.row .button .material-symbols-outlined {
  margin-right: 0;
}
</style>
